<script setup>
import {computed, onMounted, ref} from "vue";
import {useStore} from "vuex";
import {useRoute, useRouter} from "vue-router";
import { useI18n } from 'vue-i18n'
import {TEST_WINDOW_LIMITS, windowInRange, joinWindowFitsDuration} from "@/utils";
const { t } = useI18n()

const store = useStore()
const route = useRoute()
const router = useRouter()

const loading = ref(true)
const selectedSubject = ref(null)
const searchTest = ref('')
const tests = ref([])
const testDialog = ref(false)

const testActivityForm = computed(() => store.getters['Activities/testActivityForm']);
const subjects = computed(()=> store.getters['Subjects/getSubjects'])
const testName = computed(()=> testActivityForm.value.test ? testActivityForm.value.test.name : '')
const goToTestFromSeminar = computed({
  get: () => store.getters['Seminars/goToTestFromSeminar'],
  set: (newValue) => store.commit('Seminars/setGoToTestFromSeminar', newValue)
})
const fromSubjectId = computed({
  get: () => store.getters['Seminars/fromSubjectId'],
  set: (newValue) => store.commit('Seminars/setFromSubjectId', newValue)
})

const testDurations = [1, 3, 5, 7, 10]
const testHeaders = [
    { title: '', key: 'select', sortable: false, width: "1%" },
    { title: t('gamification.test_name'), align: 'start', key: 'name', width: "69%",},
    { title: t('gamification.question_count'), align: 'start', key: 'questions', width: "15%",},
    { title: t('gamification.edit'), align: 'center', sortable: false, key: 'action', width: "15%",},
]

const sessionDefaults = computed(()=> store.getters['WebSocketSession/sessionDefaults'])

onMounted(async ()=> {
  if(goToTestFromSeminar.value){
    testDialog.value = true
  }
  await store.dispatch('WebSocketSession/fetchSessionDefaults')
})

function defaultHint(value, unit){
  return value === null || value === undefined
      ? t('gamification.leave_empty_for_default')
      : t('gamification.default_value_hint', {value: value, unit: t(unit)})
}

// no min/max on the input - Vuetify would silently clamp the value on blur; show an error instead
const joinWindowRules = [
  v => windowInRange(v, TEST_WINDOW_LIMITS.join) || t('gamification.window_out_of_range', TEST_WINDOW_LIMITS.join),
  v => joinWindowFitsDuration(v, testActivityForm.value.duration) || t('gamification.join_window_longer_than_test')
]

const updateFormField = (key, value) => {
  store.commit('Activities/setTestFormField', { key, value });
};

function preventDot(e) {
  if (e.key === '.') {
    e.preventDefault()
  }
}

async function fetch(){
  loading.value = true
  await store.dispatch('Subjects/fetchAllSubjects')
  if(testActivityForm.value.test?.subjectId){
    selectedSubject.value = subjects.value.find(s => s.id === testActivityForm.value.test.subjectId)
    tests.value = await store.dispatch('Tests/fetchTestsBySubjectId', testActivityForm.value.test.subjectId)
  } else {
    let subjectId = Number(route.params.subjectId)
    if(goToTestFromSeminar.value){
      subjectId = fromSubjectId.value
    }
    selectedSubject.value = subjects.value.find(s => s.id === subjectId)
    tests.value = await store.dispatch('Tests/fetchTestsBySubjectId', subjectId)
    if(testActivityForm.value.test && !testActivityForm.value.test.subjectId){
      const fullTest = tests.value.find(t => t.id === testActivityForm.value.test.id)
      if(fullTest){
        updateFormField('test', fullTest)
      }
    }
  }
  if(goToTestFromSeminar.value){
    handleComeBack()
  }
  loading.value = false
}

function handleComeBack(){
  goToTestFromSeminar.value = false
  fromSubjectId.value = null
  if(testActivityForm.value.test){
    const updatedTest = tests.value.find(t => t.id === testActivityForm.value.test.id)
    if(updatedTest){
      updateFormField('test', updatedTest)
    }
  }
}

async function updateSelectedSubject(value){
  loading.value = true
  selectedSubject.value = value
  testActivityForm.value.test = null
  tests.value = await store.dispatch('Tests/fetchTestsBySubjectId', value.id)
  loading.value = false
}

function closeTestDialog(){
  testDialog.value = false
}

function goToTest(id){
  store.commit('Seminars/setGoToTestFromSeminar', true)
  store.commit('Seminars/setFromSubjectId', selectedSubject.value.id)
  router.push({name: 'TestDetail', params: {testId: id}})
}

</script>

<template>
  <div class="d-flex flex-column ga-8">
    <div class="d-flex align-center ga-2">
      <v-checkbox-btn :model-value="testActivityForm.separateTest" @update:model-value="value => updateFormField('separateTest', value)" color="primary" :label="$t('gamification.want_separate_qr_for_test')"/>
      <v-tooltip max-width="400">
        <template v-slot:activator="{ props }">
          <v-icon color="grey-darken-2" icon="info" v-bind="props"/>
        </template>
        <b> {{ $t('gamification.separate_test') }} </b>
        <div>
          {{ $t('gamification.separate_test_info') }}
        </div>
      </v-tooltip>
    </div>
    <div>
      <v-number-input :model-value="testActivityForm.duration" @update:model-value="value => updateFormField('duration', value)" @keypress="preventDot" hide-details :label="$t('gamification.test_duration_min')" variant="underlined" inset min="1" max="240"/>
      <div class="d-flex ga-2 mt-1">
        <v-chip class="px-5" @click="updateFormField('duration', d)" v-for="(d, index) in testDurations" :key="index" size="small" link>
          {{ d }}
        </v-chip>
      </div>

      <v-expansion-panels variant="accordion" flat bg-color="transparent" class="mt-4">
        <v-expansion-panel elevation="0">
          <v-expansion-panel-title class="px-0 text-body-2 text-medium-emphasis">
            {{ $t('gamification.advanced_test_settings') }}
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <div class="d-flex flex-column ga-4">
              <v-number-input
                  :model-value="testActivityForm.joinWindowMinutes"
                  @update:model-value="value => updateFormField('joinWindowMinutes', value)"
                  @keypress="preventDot"
                  :label="$t('gamification.join_window_label')"
                  :suffix="$t('gamification.unit_minutes')"
                  :hint="defaultHint(sessionDefaults.joinWindowMinutes, 'gamification.unit_minutes')"
                  persistent-hint
                  :rules="joinWindowRules"
                  variant="underlined" inset clearable/>
            </div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>

    <!--    DIALOG FOR CHOOSING TEST-->
    <v-dialog @after-enter="fetch" max-width="1000" v-model="testDialog">
      <template v-slot:activator="{ props: activatorProps }">
        <v-text-field :model-value="testName" v-bind="activatorProps" :placeholder="$t('gamification.select_test')" variant="underlined" readonly />
      </template>
      <v-card>
        <v-card-title class="d-flex">
          {{ $t('gamification.test_selection') }}
          <v-spacer></v-spacer>
          <v-btn @click="closeTestDialog"  icon="close" variant="text" />
        </v-card-title>
        <div class="px-5">
          <div class="d-flex ga-5">
            <v-text-field v-model="searchTest" clearable :placeholder="$t('gamification.search_test')" prepend-icon="search" density="compact" variant="underlined" hide-details />
            <v-select
                @update:model-value="updateSelectedSubject"
                :model-value="selectedSubject"
                :items="subjects"
                item-title="name"
                :loading="loading"
                variant="underlined"
                density="compact"
                return-object
                hide-details />
          </div>
          <v-radio-group
              :model-value="testActivityForm.test"
              @update:model-value="value => updateFormField('test', value)"
          >
            <v-data-table-virtual
                class="pt-5"
                :items="tests"
                :headers="testHeaders"
                :loading="loading"
                :search="searchTest"
                height="400"
                :loading-text="$t('gamification.loading')"
                :no-data-text="$t('gamification.no_data')"
            >
              <template #item.select="{ item }">
                <v-radio
                    :value="item"
                    color="primary"
                />
              </template>
              <template #item.questions="{ value }">
                {{ value.length }}
              </template>
              <template #item.action="{ item }">
                <v-btn @click="goToTest(item.id)" icon="edit" variant="text" color="grey-darken-2" />
              </template>
            </v-data-table-virtual>
          </v-radio-group>
        </div>
        <v-card-actions>
          <v-btn @click="goToTest('new')" :text="$t('gamification.create_new_test')" variant="outlined" color="primary" rounded></v-btn>
          <v-spacer></v-spacer>
          <v-btn :text="$t('gamification.save')" :disabled="!testActivityForm.test" @click="closeTestDialog" variant="flat" color="primary" rounded></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<style scoped>

</style>